/**
 * Modal de vínculo do pedido de ORIGEM (cliente roteado / cross-tenant).
 *
 * Construtor N->1 (SPEC-15 RN-04): o vendedor informa um orc_id, busca os itens
 * do pedido na origem, seleciona um item e adiciona à lista de contribuintes.
 * Pode repetir com outros orc_id / itens. Cada contribuinte tem seu produto de
 * destino resolvido via mapeamento (conferência — não assume id igual). Ao
 * confirmar, envia a lista completa de contribuintes.
 *
 * Segue o padrão de UI do projeto (overlay bg-black/50 + container bg-navy-800).
 */

import { useState } from "react";
import { X, Search, Check, Link2, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Button } from "../../../ui/Button";
import {
	lookupSourceOrderItems,
	type SourceOrderItem,
	type SourceOrderLookup,
} from "../services/crossTenantSourceOrderService";
import { resolveEntityMapping } from "../services/crossTenantMappingService";
import { checkSourceItemLinked } from "../services/crossTenantLinkService";

/** Contribuinte de origem adicionado ao vínculo (com conferência de destino). */
interface Contributor {
	orcId: number;
	oitId: number;
	proId: number | null;
	productName: string | null;
	role: string | null;
	/** Destino resolvido pelo mapeamento (conferência). */
	target: { entityId: number } | null;
	/** Erro de resolução (ex.: sem mapeamento) — não bloqueia o vínculo. */
	mappingError: string | null;
	resolving: boolean;
	/** Aviso: este item de origem já possui vínculo (SPEC-17) — não bloqueia. */
	alreadyLinked: { targetOrcId: number } | null;
}

export interface CrossTenantLinkContributor {
	orcId: number;
	oitId: number;
	role?: string | null;
}

interface CrossTenantLinkModalProps {
	isOpen: boolean;
	/** Rótulo do tenant de origem, só para exibição (ex.: "oneuniformes"). */
	sourceTenantLabel?: string | null;
	onConfirm: (sources: CrossTenantLinkContributor[]) => void;
	onCancel: () => void;
}

export function CrossTenantLinkModal({
	isOpen,
	sourceTenantLabel,
	onConfirm,
	onCancel,
}: CrossTenantLinkModalProps) {
	const [orcIdInput, setOrcIdInput] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [searchError, setSearchError] = useState<string | null>(null);
	const [order, setOrder] = useState<SourceOrderLookup | null>(null);
	const [selectedOitId, setSelectedOitId] = useState("");
	const [contributors, setContributors] = useState<Contributor[]>([]);

	if (!isOpen) return null;

	const orcId = Number(orcIdInput);
	const canSearch = Number.isInteger(orcId) && orcId > 0 && !isSearching;
	const selectedItem: SourceOrderItem | null =
		order?.items.find((it) => it.oitId === Number(selectedOitId)) ?? null;
	const alreadyAdded = (o: number, oit: number) =>
		contributors.some((c) => c.orcId === o && c.oitId === oit);
	const canAdd =
		Boolean(order?.orderFound && selectedItem) &&
		!alreadyAdded(order?.orcId ?? 0, Number(selectedOitId));
	const canConfirm = contributors.length > 0;

	const handleSearch = async () => {
		if (!canSearch) return;
		setIsSearching(true);
		setSearchError(null);
		setOrder(null);
		setSelectedOitId("");
		try {
			const lookup = await lookupSourceOrderItems(orcId);
			setOrder(lookup);
			if (lookup.orderFound && lookup.items.length > 0) {
				setSelectedOitId(String(lookup.items[0].oitId));
			}
		} catch (error) {
			setSearchError(
				error instanceof Error
					? error.message
					: "Falha ao buscar o pedido de origem. Tente novamente.",
			);
		} finally {
			setIsSearching(false);
		}
	};

	const resolveContributorTarget = async (key: string, proId: number | null) => {
		if (proId == null) {
			updateContributor(key, {
				resolving: false,
				mappingError: "Item sem produto para resolver.",
			});
			return;
		}
		try {
			const result = await resolveEntityMapping({
				entityType: "produto",
				sourceEntityId: proId,
			});
			const target = result.targets[0] ?? null;
			updateContributor(key, {
				resolving: false,
				target: target ? { entityId: target.entityId } : null,
				mappingError: target ? null : "Mapeamento sem destino.",
			});
		} catch (error) {
			updateContributor(key, {
				resolving: false,
				mappingError:
					error instanceof Error ? error.message : "Sem mapeamento para o produto.",
			});
		}
	};

	const contributorKey = (o: number, oit: number) => `${o}:${oit}`;

	const updateContributor = (key: string, patch: Partial<Contributor>) => {
		setContributors((prev) =>
			prev.map((c) =>
				contributorKey(c.orcId, c.oitId) === key ? { ...c, ...patch } : c,
			),
		);
	};

	const handleAdd = () => {
		if (!order || !selectedItem || !canAdd) return;
		const contributor: Contributor = {
			orcId: order.orcId,
			oitId: selectedItem.oitId,
			proId: selectedItem.proId,
			productName: selectedItem.productName,
			role: null,
			target: null,
			mappingError: null,
			resolving: true,
			alreadyLinked: null,
		};
		setContributors((prev) => [...prev, contributor]);
		const key = contributorKey(contributor.orcId, contributor.oitId);
		void resolveContributorTarget(key, contributor.proId);
		void checkContributorAlreadyLinked(key, contributor.orcId, contributor.oitId);
	};

	// Aviso de duplicidade (SPEC-17): best-effort. Falha aqui NÃO impede o vínculo
	// nem trava a adição — apenas deixa de exibir o aviso.
	const checkContributorAlreadyLinked = async (
		key: string,
		orcId: number,
		oitId: number,
	) => {
		try {
			const res = await checkSourceItemLinked(orcId, oitId);
			if (res.linked && res.links.length > 0) {
				updateContributor(key, {
					alreadyLinked: { targetOrcId: res.links[0].targetOrcId },
				});
			}
		} catch {
			// silencioso: aviso é opcional
		}
	};

	const handleRemove = (key: string) => {
		setContributors((prev) =>
			prev.filter((c) => contributorKey(c.orcId, c.oitId) !== key),
		);
	};

	const formatMoney = (value: number | null): string =>
		value === null
			? "--"
			: value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

	return (
		<div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
			<div className="bg-navy-800 border border-navy-700 rounded-lg w-full max-w-[440px] shadow-2xl max-h-[90vh] flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between p-3 border-b border-navy-700">
					<div className="flex items-center gap-2">
						<div className="p-1.5 bg-violet-500/20 rounded-lg">
							<Link2 className="w-4 h-4 text-violet-300" />
						</div>
						<span className="text-sm font-semibold text-white">
							Vincular pedidos de origem
						</span>
					</div>
					<Button variant="ghost" size="xs" onClick={onCancel}>
						<X className="w-4 h-4" />
					</Button>
				</div>

				{/* Content */}
				<div className="p-4 space-y-4 overflow-y-auto">
					<p className="text-xs text-gray-400">
						Informe um pedido
						{sourceTenantLabel ? ` da ${sourceTenantLabel}` : " da origem"},
						busque os itens e adicione os que compõem este pedido. Você pode
						adicionar itens de mais de um pedido.
					</p>

					{/* Busca por orc_id */}
					<div className="flex gap-2 items-end">
						<div className="space-y-1 flex-1">
							<label className="text-[11px] text-gray-300">Pedido (orc_id)</label>
							<input
								type="number"
								inputMode="numeric"
								min={1}
								value={orcIdInput}
								onChange={(event) => setOrcIdInput(event.target.value)}
								placeholder="ex.: 7"
								className="w-full rounded-lg border border-navy-600 bg-navy-900 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-violet-400/50"
							/>
						</div>
						<Button
							variant="secondary"
							size="sm"
							icon={Search}
							onClick={handleSearch}
							disabled={!canSearch}
						>
							{isSearching ? "..." : "Buscar"}
						</Button>
					</div>

					{searchError && (
						<div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
							<AlertTriangle className="w-3.5 h-3.5 text-red-300 shrink-0 mt-0.5" />
							<p className="text-[11px] text-red-200">{searchError}</p>
						</div>
					)}

					{order && !order.orderFound && (
						<div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
							<AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
							<p className="text-[11px] text-amber-200">
								Pedido #{orcId} não encontrado na origem. Confira o número.
							</p>
						</div>
					)}

					{/* Seleção de item do pedido encontrado */}
					{order?.orderFound && (
						<div className="rounded-lg border border-navy-600 bg-navy-900/60 p-3 space-y-2">
							<div className="text-[11px] text-gray-400">
								Pedido #{order.orcId}
								{order.clientName ? ` · ${order.clientName}` : ""}
							</div>
							<div className="flex gap-2 items-end">
								<div className="space-y-1 flex-1">
									<label className="text-[11px] text-gray-300">Item</label>
									<select
										value={selectedOitId}
										onChange={(e) => setSelectedOitId(e.target.value)}
										className="w-full rounded-lg border border-navy-600 bg-navy-900 px-2 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-violet-400/50"
									>
										{order.items.length === 0 && <option value="">Sem itens</option>}
										{order.items.map((it) => (
											<option key={it.oitId} value={it.oitId}>
												I-{it.oitId} · {it.productName ?? "produto"} ·{" "}
												{it.quantidade ?? "--"} un
											</option>
										))}
									</select>
								</div>
								<Button
									variant="secondary"
									size="sm"
									onClick={handleAdd}
									disabled={!canAdd}
									aria-label="Adicionar item"
								>
									<Plus className="w-4 h-4" />
								</Button>
							</div>
							{selectedItem && (
								<div className="text-[11px] text-gray-400">
									Valor do item: {formatMoney(selectedItem.valorTotalItem)}
									{alreadyAdded(order.orcId, Number(selectedOitId)) && (
										<span className="text-amber-300"> · já adicionado</span>
									)}
								</div>
							)}
						</div>
					)}

					{/* Lista de contribuintes com conferência de destino */}
					{contributors.length > 0 && (
						<div className="space-y-2">
							<div className="text-[11px] font-medium text-gray-300 uppercase tracking-wide">
								Contribuintes ({contributors.length})
							</div>
							{contributors.map((c) => {
								const key = contributorKey(c.orcId, c.oitId);
								return (
									<div
										key={key}
										className="rounded-lg border border-navy-600 bg-navy-900/60 p-2.5 flex items-start gap-2"
									>
										<div className="flex-1 min-w-0">
											<div className="text-xs text-white font-medium truncate">
												#{c.orcId} / I-{c.oitId} · {c.productName ?? "produto"}
												{c.role ? (
													<span className="text-gray-400"> ({c.role})</span>
												) : null}
											</div>
											{c.resolving && (
												<div className="text-[11px] text-gray-400">
													Resolvendo destino…
												</div>
											)}
											{!c.resolving && c.target && (
												<div className="flex items-center gap-1 text-[11px] text-emerald-300">
													<Check className="w-3 h-3" />
													Destino: produto #{c.target.entityId}
												</div>
											)}
											{!c.resolving && c.mappingError && (
												<div className="flex items-center gap-1 text-[11px] text-amber-300">
													<AlertTriangle className="w-3 h-3" />
													{c.mappingError}
												</div>
											)}
											{c.alreadyLinked && (
												<div className="flex items-center gap-1 text-[11px] text-amber-300">
													<AlertTriangle className="w-3 h-3 shrink-0" />
													Este item já está vinculado ao pedido O-
													{c.alreadyLinked.targetOrcId}. Você pode continuar
													mesmo assim.
												</div>
											)}
										</div>
										<button
											type="button"
											onClick={() => handleRemove(key)}
											className="text-gray-400 hover:text-red-300 transition-colors p-1"
											aria-label="Remover contribuinte"
										>
											<Trash2 className="w-3.5 h-3.5" />
										</button>
									</div>
								);
							})}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="flex gap-2 p-3 border-t border-navy-700">
					<Button
						variant="secondary"
						size="sm"
						onClick={onCancel}
						className="flex-1"
					>
						Cancelar
					</Button>
					<Button
						variant="primary"
						size="sm"
						icon={Check}
						onClick={() => {
							if (canConfirm) {
								onConfirm(
									contributors.map((c) => ({
										orcId: c.orcId,
										oitId: c.oitId,
										role: c.role,
									})),
								);
							}
						}}
						disabled={!canConfirm}
						className="flex-1"
					>
						Confirmar vínculo
					</Button>
				</div>
			</div>
		</div>
	);
}
